import config from 'src/config';
import MomentUtils from '@date-io/moment';
import kurkleColor from '@kurkle/color';
import urljoin from 'url-join';

import titilerParams from 'src/utils/titilerParams';


const { titilerBaseUrl, tiffsAssetsBaseUrl } = config;

const { moment } = new MomentUtils();
moment.locale('es');


const objectIsEmpty = object => Object.entries(object).length === 0 && object.constructor === Object;

const arraysAreEqual = (arr1, arr2) => arr1.length === arr2.length && arr1.every((value, idx) => value === arr2[idx]);

const humanReadableArrayOfStrings = (arrayOfStrings, connector = 'y') => arrayOfStrings.join(', ').replace(/(.*),/, `$1 ${connector}`);

const truncateWithEllipsis = (string = '', charNumber = 5) => string.length > charNumber ? `${string.substring(0, charNumber)}...` : string;

// with thanks to https://stackoverflow.com/a/55756548
const tryParseXml = src => {
  /* returns an XMLDocument, or null if `src` is malformed */
  const key = `a${Math.random().toString(32)}`;
  const parser = new DOMParser();
  let doc = null;
  try {
    doc = parser.parseFromString(`${src}<?${key}?>`, `application/xml`);
  // eslint-disable-next-line
  } catch (_) {}

  if (!(doc instanceof XMLDocument)) {
    return null;
  }

  const lastNode = doc.lastChild;
  if (!(lastNode instanceof ProcessingInstruction) || lastNode.target !== key || lastNode.data !== ``) {
    return null;
  }

  doc.removeChild(lastNode);

  const errElemCount = doc.documentElement.getElementsByTagName(`parsererror`).length;
  if (errElemCount !== 0) {
    let errDoc = null;
    try {
      errDoc = parser.parseFromString(`${src}<?`, `application/xml`);
      // eslint-disable-next-line
    } catch (_) {}

    if (!(errDoc instanceof XMLDocument) || errDoc.documentElement.getElementsByTagName(`parsererror`).length === errElemCount) {
      return null;
    }
  }

  return doc;
};

// Note: imgFiles debe estar al final del formulario para que lleguen todos los datos
const createForm = data => {
  const form = new FormData();
  let imgFiles = [];
  Object.keys(data).forEach(key => {
    if (data[key] !== undefined) {
      if (Array.isArray(data[key])) {
        if (key === 'imgFiles') {
          imgFiles = data[key];
        } else {
          data[key].forEach(d => form.append(key, d));
        }
      } else {
        form.append(key, data[key]);
      }
    }
  });

  if (imgFiles.length > 0) {
    imgFiles.forEach(image => form.append('imgFile', image, image.filename));
  }

  return form;
};

const getMaintenanceStatus = () => {
  const { start, finish } = config.maintenanceDates;
  const startDate = new Date(start);
  const finishDate = new Date(finish);
  const currentDate = new Date();

  const isMaintenanceScheduled = currentDate < startDate;
  const isMaintenanceOn = startDate < currentDate && currentDate < finishDate;
  const isMaintenanceScheduledOrOn = isMaintenanceScheduled || isMaintenanceOn;

  const hrStartDate = _formatHumanDateEs(startDate);
  const hrFinishDate = _formatHumanDateEs(finishDate);

  return { startDate, finishDate, hrStartDate, hrFinishDate, isMaintenanceScheduled, isMaintenanceOn, isMaintenanceScheduledOrOn };
};

const isNullOrUndefined = v => v === null || v === undefined;

const isEmpty = v => isNullOrUndefined(v) || v === '';


const nviroPalette = [
  '#FFDF00', '#29ABE2', '#ED3F79',
  '#FBB03B', '#C0D72E', '#5BD6E6',
  '#6C757A', '#40474B', '#5193EB',
  '#8BC63D', '#F7931E', '#BD1860',
  '#2F89D1', '#FBCD44', '#ABABAB',
];

// menos colores, pero contrastan mejor
const nviroPaletteB = [
  '#5BD6E6',
  '#2F89D1',
  '#ED3F79',
  '#6C757A',
  '#8BC63D',
  '#F7931E',
  '#FBCD44',
];

const getRandomNviroColor = (palette = nviroPalette) => palette[Math.floor(Math.random() * palette.length)];

const getRandomNviroColors = (count = nviroPaletteB.length) => {

  if (count > nviroPaletteB.length) {
    throw Error (`Paleta insuficiente para cantidad de colores ingresada`);
  }

  const colors = [];
  let length = 0;
  while (length < count) {
    const color = getRandomNviroColor(nviroPaletteB);
    if (!colors.includes(color)) {
      colors.push(color);
      length++;
    }
  }
  return colors;
};

const transparentize = (value, opacity) => {
  const alpha = opacity === undefined ? 0.5 : 1 - opacity;
  return kurkleColor(value).alpha(alpha).rgbString();
};

const darkenize = (value, dark) => {
  const alpha = dark === undefined ? 0.5 : dark;
  return kurkleColor(value).darken(alpha).rgbString();
};

const removeUndefinedPropsFromObject = obj => Object.fromEntries(Object.entries(obj).filter(([ , v ]) => v != null));

const deepObjClone = obj => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepObjClone(item));
  }

  const clone = {};
  for (const key in obj) {
    clone[key] = deepObjClone(obj[key]);
  }

  return clone;
};

const removeDuplicatedFromArray = array => [ ...new Set(array) ];

const getRandomBetweenRange = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const urlJoin = (...args) => {
  if (args.some(arg => isNullOrUndefined(arg))) {
    throw Error('No deben haber argumentos nulos o indefinidos en la URL');
  }
  const formatedArgs = args.map(arg => typeof arg === 'number' ? arg.toString() : arg);

  const url = urljoin(...formatedArgs);

  return url;
};

const thousandsDotSeparator = number => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const thousandsDotAndDecimalComma = number => {
  const [ integer, decimal ] = number.toString().split('.');
  return `${thousandsDotSeparator(integer)},${decimal}`;
};

const titilerUrl = urlJoin(titilerBaseUrl, '/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png');

const buildTiTilerUrl = ({ filePath, assetType, rescale }) => {
  const paramsObj = titilerParams[assetType];

  if (!paramsObj) {
    console.warn(`AssetType '${assetType}' no está soportado en titilerParams. Usando configuración por defecto.`);
  }

  const paramsArr = Object.entries(paramsObj ?? titilerParams.default).flatMap(
    ([ key, value ]) => Array.isArray(value)
      ? value.map(v => `${key}=${encodeURIComponent(v)}`)
      : [ `${key}=${encodeURIComponent(typeof value === 'object' ? JSON.stringify(value) : value)}` ],
  );

  const rescaleParams = Array.isArray(rescale)
    ? rescale.map(r => `rescale=${encodeURIComponent(r)}`)
    : rescale ? [ `rescale=${rescale}` ] : [];

  const paramsStr = [
    `url=${encodeURIComponent(urlJoin(tiffsAssetsBaseUrl, filePath))}`,
    ...paramsArr,
    ...rescaleParams,
  ].join('&');

  return `${titilerUrl}?${paramsStr}`;
};

const getMonthString = monthNumber => {
  if (monthNumber < 1 || monthNumber > 12) {
    return 'Mes inválido';
  }
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  return months[monthNumber - 1];
};

const _formatHumanDateEs = date => moment(date).format('dddd DD [de] MMMM [de] YYYY [a las] HH:mm');

const __internal = process.env.NODE_ENV === 'test' ?
  {
    _formatHumanDateEs,
  } :
  undefined;

const stringToCamelCase = string => string.replace(/[^a-zA-Z0-9]+(.)?/g, (_, chr) => chr ? chr.toUpperCase() : '');

const formatDashedString = string => {
  if (typeof string !== 'string' || string.length === 0) {
    return '';
  }
  return string
    .split('-')
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};


export {
  objectIsEmpty,
  arraysAreEqual,
  humanReadableArrayOfStrings,
  truncateWithEllipsis,
  tryParseXml,
  createForm,
  getMaintenanceStatus,
  isEmpty,
  isNullOrUndefined,
  getRandomNviroColor,
  getRandomNviroColors,
  transparentize,
  darkenize,
  removeUndefinedPropsFromObject,
  deepObjClone,
  removeDuplicatedFromArray,
  getRandomBetweenRange,
  urlJoin,
  thousandsDotAndDecimalComma,
  thousandsDotSeparator,
  buildTiTilerUrl,
  getMonthString,
  stringToCamelCase,
  formatDashedString,
};

export { __internal };
